import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, RefreshCw, ExternalLink, Play, Check, AlertTriangle, Zap } from 'lucide-react';
import { ScoreRing, SeverityBadge } from './SEOOSShared';
import { Button } from '@/components/ui/button';

const API = '';

export default function SEOOSReports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/seoos/report`, { headers }).then(r => r.json()).then(d => { setReport(d.error ? null : d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    // Run full audit first, then generate report
    await fetch(`${API}/api/seoos/run-audit`, { method: 'POST', headers });
    await fetch(`${API}/api/seoos/report/generate`, { method: 'POST', headers });
    load();
    setGenerating(false);
  };

  const downloadHtml = () => { window.open(`${API}/api/seoos/report/html`, '_blank'); };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="seoos-reports">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          <FileText className="w-5 h-5 inline mr-2 text-purple-400" />Executive SEO Raporu
        </h2>
        <div className="flex gap-2">
          {report && <Button onClick={downloadHtml} variant="outline" size="sm" className="border-slate-700 text-slate-400"><Download className="w-3.5 h-3.5 mr-1" />HTML Indir</Button>}
          <Button onClick={generate} disabled={generating} className="bg-purple-600 hover:bg-purple-500 text-white text-xs">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${generating ? 'animate-spin' : ''}`} />{generating ? 'Olusturuluyor...' : 'Rapor Olustur'}
          </Button>
        </div>
      </div>

      {!report ? (
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-16 text-center">
          <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">Henuz rapor olusturulmadi</p>
          <p className="text-xs text-slate-600 mt-1">"Rapor Olustur" butonuna tiklayarak baslayın</p>
        </div>
      ) : (
        <>
          {/* Master Score */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3 bg-gradient-to-br from-slate-900 via-purple-950/20 to-slate-900 border border-purple-500/20 rounded-2xl p-6 flex flex-col items-center">
              <ScoreRing score={Math.round(report.master_score || 0)} size={120} label="Master Skor" />
              <p className="text-xs text-slate-500 mt-2">{report.generated_at?.slice(0, 10)}</p>
            </div>
            <div className="col-span-9 grid grid-cols-5 gap-3">
              {Object.entries(report.scores || {}).map(([key, val]) => {
                const labels = { technical: 'Teknik', content: 'Icerik', authority: 'Otorite', crawl: 'Tarama', index: 'Indeks' };
                return (
                  <div key={key} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4 text-center">
                    <p className={`text-2xl font-black ${val >= 60 ? 'text-emerald-400' : val >= 40 ? 'text-amber-400' : 'text-red-400'}`}>{Math.round(val)}</p>
                    <p className="text-[10px] text-slate-500 uppercase">{labels[key] || key}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Analysis */}
          <div className="bg-gradient-to-br from-purple-950/20 to-slate-900 border border-purple-500/20 rounded-xl p-5">
            <h3 className="text-sm font-bold text-purple-300 uppercase mb-3">AI Master Analizi</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{report.ai_analysis}</p>
          </div>

          {/* Roadmap */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white uppercase">Fix Yol Haritasi</h3>
            {["P0", "P1", "P2"].map(p => {
              const items = report.roadmap?.[p] || [];
              if (!items.length) return null;
              const colors = { P0: 'red', P1: 'orange', P2: 'amber' };
              return (
                <div key={p}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border bg-${colors[p]}-500/10 text-${colors[p]}-400 border-${colors[p]}-500/30`}>{p}</span>
                    <span className="text-xs text-slate-500">{items.length} gorev</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((item, i) => (
                      <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-lg p-3">
                        <p className="text-sm font-bold text-white">{item.title}</p>
                        <p className="text-xs text-slate-400 mt-1">{item.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-[10px]">
                          <span className="text-emerald-400">{item.impact}</span>
                          <span className="text-slate-600">{item.affected_pages} sayfa</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bookmaker Analysis */}
          {report.bookmaker_analysis?.length > 0 && (
            <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/30"><h3 className="text-sm font-bold text-white uppercase">Bahisci SEO Analizi</h3></div>
              <table className="w-full text-xs">
                <thead><tr className="text-slate-500 border-b border-slate-800/30">
                  <th className="text-left px-4 py-2">Bahisci</th><th className="text-center px-2">Otorite</th><th className="text-center px-2">Icerik</th><th className="text-center px-2">Ic Link</th><th className="text-center px-2">Skor</th>
                </tr></thead>
                <tbody>
                  {report.bookmaker_analysis.slice(0, 15).map((b, i) => (
                    <tr key={i} className="border-b border-slate-800/20">
                      <td className="px-4 py-2 text-slate-300 font-medium">{b.name}</td>
                      <td className={`text-center px-2 ${b.authority > 30 ? 'text-emerald-400' : 'text-red-400'}`}>{b.authority}</td>
                      <td className="text-center px-2 text-slate-400">{b.content_length}</td>
                      <td className="text-center px-2 text-slate-400">{b.internal_links}</td>
                      <td className={`text-center px-2 font-bold ${b.content_score > 50 ? 'text-emerald-400' : 'text-amber-400'}`}>{b.content_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
